import { MoneyTotal, ReturnAnalyticsReport } from 'types/returns-report';

export type ReturnReportBreakdown =
  | 'SKU_REASON'
  | 'PRODUCT_LINE'
  | 'MONTHLY'
  | 'REQUESTED_OUTCOME'
  | 'RETURN_ITEMS';

export type ReturnReportCell = string | number;

export interface ReturnReportTable {
  title: string;
  fileLabel: string;
  columns: string[];
  rows: ReturnReportCell[][];
}

const displayNumber = (value: number) =>
  Number.isInteger(value) ? value : Number(value.toFixed(2));

export const formatMoneyTotals = (totals: MoneyTotal[] = []) => {
  const values = totals
    .filter(
      ({ amount, currency }) =>
        Number.isFinite(Number(amount)) && Boolean(currency?.trim()),
    )
    .slice()
    .sort((left, right) => left.currency.localeCompare(right.currency))
    .map(
      ({ amount, currency }) =>
        `${currency.trim().toUpperCase()} ${Number(amount).toFixed(2)}`,
    );

  return values.length ? values.join('; ') : '—';
};

const formatMoney = (amount: number | null, currency: string | null) =>
  amount != null && Number.isFinite(Number(amount)) && currency?.trim()
    ? `${currency.trim().toUpperCase()} ${Number(amount).toFixed(2)}`
    : '—';

export const formatReportRate = (rate: number | null) =>
  rate == null || !Number.isFinite(Number(rate))
    ? '—'
    : `${displayNumber(Number(rate))}%`;

const formatRateChange = (change: number | null) => {
  if (change == null || !Number.isFinite(Number(change))) return '—';
  const value = displayNumber(Number(change));
  return `${value > 0 ? '+' : ''}${value} pp`;
};

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return value;
  return `${parsed.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

const formatDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf())
    ? value
    : parsed.toISOString().slice(0, 10);
};

const formatExclusiveEndDate = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return value;
  return new Date(parsed.valueOf() - 1).toISOString().slice(0, 10);
};

const formatLabel = (value: string | null) => {
  if (!value) return '—';
  const normalized = value.trim().replace(/[_-]+/g, ' ').toLowerCase();
  return normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : '—';
};

const formatProductLine = (
  report: ReturnAnalyticsReport,
  value: string | null,
) =>
  report.catalog.status === 'UNAVAILABLE'
    ? 'Unavailable'
    : value || 'Uncategorized';

export const buildReturnReportTable = (
  report: ReturnAnalyticsReport,
  breakdown: ReturnReportBreakdown,
): ReturnReportTable => {
  if (breakdown === 'REQUESTED_OUTCOME') {
    const groups = new Map<
      string,
      { requestIds: Set<string>; itemCount: number; quantity: number }
    >();
    report.details.forEach((item) => {
      const outcome = item.requestedResolution?.trim() || '';
      const group = groups.get(outcome) || {
        requestIds: new Set<string>(),
        itemCount: 0,
        quantity: 0,
      };
      group.requestIds.add(item.returnId);
      group.itemCount += 1;
      group.quantity += item.quantity;
      groups.set(outcome, group);
    });
    return {
      title: 'Requested outcome',
      fileLabel: 'requested-outcome',
      columns: [
        'Requested outcome',
        'Requests',
        'Request items',
        'Requested quantity',
      ],
      rows: Array.from(groups.entries())
        .sort(([left], [right]) =>
          (left || 'Unknown').localeCompare(right || 'Unknown'),
        )
        .map(([outcome, group]) => [
          outcome ? formatLabel(outcome) : 'Unknown',
          group.requestIds.size,
          group.itemCount,
          group.quantity,
        ]),
    };
  }

  if (breakdown === 'PRODUCT_LINE') {
    return {
      title: 'Product line',
      fileLabel: 'product-line',
      columns: [
        'Product line',
        'Requests',
        'Orders with requests',
        'Requested quantity',
        'Fulfilled orders',
        'Request activity ratio',
        'Original item value',
        'Label cost',
      ],
      rows: report.productLines.map((line) => [
        line.productLine,
        line.returnCount,
        line.returnedOrders,
        line.returnedQuantity,
        line.fulfilledOrders,
        formatReportRate(line.rate),
        formatMoneyTotals(line.values),
        formatMoneyTotals(line.labelCosts),
      ]),
    };
  }

  if (breakdown === 'MONTHLY') {
    return {
      title: 'Monthly',
      fileLabel: 'monthly',
      columns: [
        'Month',
        'From (UTC)',
        'Through (UTC)',
        'Requests',
        'Orders with requests',
        'Requested quantity',
        'Fulfilled orders',
        'Request activity ratio',
        'Previous month ratio',
        'Change',
        'Original item value',
        'Label cost',
      ],
      rows: report.monthly.map((month) => [
        month.month,
        formatDate(month.startDate),
        formatExclusiveEndDate(month.endDate),
        month.returnCount,
        month.returnedOrders,
        month.returnedQuantity,
        month.fulfilledOrders,
        formatReportRate(month.rate),
        formatReportRate(month.previousRate),
        formatRateChange(month.rateChange),
        formatMoneyTotals(month.values),
        formatMoneyTotals(month.labelCosts),
      ]),
    };
  }

  if (breakdown === 'RETURN_ITEMS') {
    return {
      title: 'Request items',
      fileLabel: 'return-items',
      columns: [
        'Request ID',
        'Request item ID',
        'Request created',
        'Delivered to warehouse',
        'Order ID',
        'Order #',
        'Original SKU',
        'Original product',
        'Product line',
        'Reason',
        'Requested quantity',
        'Recorded resolution',
        'Requested outcome',
        'Original item value',
        'Label cost',
      ],
      rows: report.details.map((item) => [
        item.returnId,
        item.returnItemId,
        formatDateTime(item.returnCreatedAt),
        formatDateTime(item.returnDeliveredAt),
        item.orderId,
        item.orderNumber || '—',
        item.sku || 'No SKU',
        item.product || 'Unknown product',
        formatProductLine(report, item.productLine),
        formatLabel(item.reason),
        item.quantity,
        formatLabel(item.resolution),
        formatLabel(item.requestedResolution),
        formatMoney(item.value, item.currency),
        formatMoney(item.labelCost, item.labelCurrency),
      ]),
    };
  }

  return {
    title: 'SKU × reason',
    fileLabel: 'sku-by-reason',
    columns: [
      'Original SKU',
      'Original product',
      'Product line',
      'Reason',
      'Requests',
      'Requested quantity',
      'Original item value',
      'Label cost',
    ],
    rows: report.skuReasons.map((item) => [
      item.sku || 'No SKU',
      item.product || 'Unknown product',
      formatProductLine(report, item.productLine),
      formatLabel(item.reason),
      item.returnCount,
      item.returnedQuantity,
      formatMoneyTotals(item.values),
      formatMoneyTotals(item.labelCosts),
    ]),
  };
};

const csvCell = (value: ReturnReportCell) => {
  let text = String(value);
  if (typeof value === 'string' && /^[\t\r\n ]*[=+\-@]/.test(text)) {
    text = `'${text}`;
  }
  const escaped = text.replace(/"/g, '""');
  return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
};

export const buildReturnReportCsv = (table: ReturnReportTable) =>
  [table.columns, ...table.rows]
    .map((row) => row.map(csvCell).join(','))
    .join('\n');
